import { useState, useEffect } from 'react';
import useTabState from '../hooks/useTabState';
import PageLayout from '../components/ui/PageLayout';
import TabBar from '../components/ui/TabBar';
import TrialBalance from './TrialBalance';
import BalanceSheet from './BalanceSheet';
import IncomeStatement from './IncomeStatement';
import CashFlowStatement from './CashFlowStatement';
import Conversion from './Conversion';
// PostedJEs removed 2026-05-15 -- consolidated into GL Workbench -> Journal Entries.
import { API_V1, authFetch } from '../constants';
import { useEntities } from '../hooks/useEntities';
import { useGlobalEntity } from '../hooks/useGlobalEntity';
import Modal from '../components/ui/Modal';

const TABS = [
  'Trial Balance', 'Balance Sheet', 'P&L', 'Cash Flow',
  'Consolidated', 'Conversion',
] as const;
type Tab = typeof TABS[number];

const TAB_KEYS: Record<string, Tab> = {
  trial_balance: 'Trial Balance',
  balance_sheet: 'Balance Sheet',
  'p&l': 'P&L',
  cash_flow: 'Cash Flow',
  consolidated: 'Consolidated',
  conversion: 'Conversion',
};

export default function FinancialReports() {
  const [activeTab, setActiveTab] = useTabState<Tab>('tab', TABS, 'Trial Balance');
  const entities = useEntities();
  const { entity: globalEntity } = useGlobalEntity();
  const [showExport, setShowExport] = useState(false);
  const [exportEntity, setExportEntity] = useState(globalEntity || '');
  useEffect(() => { if (globalEntity) setExportEntity(globalEntity); }, [globalEntity]);
  const [exportPeriod, setExportPeriod] = useState('');
  const [exporting, setExporting] = useState(false);

  const activeKey = activeTab.toLowerCase().replace(/\s+/g, '_');

  const handleExportFinancials = async () => {
    if (!exportEntity || !exportPeriod) return;
    setExporting(true);
    try {
      const p = new URLSearchParams({ entity_code: exportEntity, period: exportPeriod, format: 'csv' });
      const resp = await authFetch(`${API_V1}/reports/financials/export?${p}`);
      if (!resp.ok) throw new Error('Export failed');
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `financials-${exportEntity}-${exportPeriod}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      setShowExport(false);
    } catch (e) { console.error(e); }
    setExporting(false);
  };

  return (
    <PageLayout
      title="Financial Reports"
      subtitle="Trial balance, financial statements, and journal entry review"
      action={{ label: 'Export Financials', onClick: () => setShowExport(true), variant: 'secondary' }}
    >
      <TabBar
        tabs={TABS as unknown as string[]}
        active={activeKey}
        onChange={k => { if (TAB_KEYS[k]) setActiveTab(TAB_KEYS[k]); }}
      />

      <Modal open={showExport} onClose={() => setShowExport(false)} title="Export Financial Statements" size="sm"
        footer={
          <>
            <button onClick={() => setShowExport(false)} style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid var(--border-default)', background: 'var(--bg-card)', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
            <button onClick={handleExportFinancials} disabled={exporting || !exportEntity || !exportPeriod} style={{ padding: '8px 16px', borderRadius: 6, background: 'var(--accent)', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', border: 'none', opacity: exportEntity && exportPeriod ? 1 : 0.5 }}>
              {exporting ? 'Exporting...' : 'Export CSV'}
            </button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Entity</label>
            <select value={exportEntity} onChange={e => setExportEntity(e.target.value)} style={{ padding: '8px 12px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 13, width: '100%', background: 'var(--bg-card)', color: 'var(--text-primary)' }}>
              <option value="">Select entity...</option>
              {entities.map(e => <option key={e.entity_code} value={e.entity_code}>{e.entity_code} - {e.entity_name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Period</label>
            <input type="month" value={exportPeriod} onChange={e => setExportPeriod(e.target.value)} style={{ padding: '8px 12px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 13, width: '100%', background: 'var(--bg-card)', color: 'var(--text-primary)' }} />
          </div>
          <p style={{ fontSize: 11, color: 'var(--text-secondary)', margin: 0 }}>Exports Balance Sheet + Income Statement as a single CSV file.</p>
        </div>
      </Modal>

      {activeTab === 'Trial Balance' && <TrialBalance />}
      {activeTab === 'Balance Sheet' && <BalanceSheet />}
      {activeTab === 'P&L' && <IncomeStatement />}
      {activeTab === 'Cash Flow' && <CashFlowStatement />}
      {activeTab === 'Consolidated' && <TrialBalance />}
      {activeTab === 'Conversion' && <Conversion />}
    </PageLayout>
  );
}
